import { type FC, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  AppBar,
  TablePagination,
  CircularProgress,
  Checkbox,
  Chip,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  usePlatformCustomCoversQuery,
  useCurrentPlatformQuery,
} from "@/partner/entities/platforms";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { CustomCoversListToolbar } from "./CustomCoversListToolbar";
import Card from "@mui/material/Card";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  backgroundColor: selected
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  padding: theme.spacing(2, 1),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

interface CustomCoversListProps {
  onEditCover: (id: string) => void;
}

export const CustomCoversList: FC<CustomCoversListProps> = ({
  onEditCover,
}) => {
  const { t } = useTranslation();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search },
  } = useFilter();

  const { data: platformData } = useCurrentPlatformQuery();
  const currentPlatform = platformData?.currentPlatform;

  const { data, loading } = usePlatformCustomCoversQuery({
    variables: {
      page,
      rowsPerPage,
      filter: currentPlatform
        ? {
            platformId: currentPlatform.id,
            search: search || undefined,
          }
        : undefined,
    },
  });

  const covers = data?.platformCustomCovers?.nodes || [];
  const count = data?.platformCustomCovers?.count || 0;

  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const selectedIds = selectedItems.map((item) => item.id);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(covers.map((c) => ({ id: c.id })));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      setSelectedItems([...selectedItems, { id }]);
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < covers.length;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (covers.length === 0) {
    return <EmptyListCard title={t("platformCustomCovers.list.empty")} />;
  }

  return (
    <Box>
      <AppBar position="static" color="default" elevation={0}>
        <CustomCoversListToolbar
          indeterminate={indeterminate}
          numSelected={numSelected}
          nodes={covers}
          onSelectAllClick={handleSelectAll}
          selectedItems={selectedItems}
          onCloseSelected={() => setSelectedItems([])}
        />
      </AppBar>

      <Grid container spacing={1} sx={{ mt: 1 }}>
        {covers.map((cover, index) => {
          const isSelected = selectedIds.includes(cover.id);
          return (
            <Grid key={cover.id} size={12}>
              <StyledCard
                selected={isSelected}
                elevation={0}
                onClick={() => onEditCover(cover.id)}
              >
                <Grid container spacing={2} wrap="nowrap" alignItems="center">
                  <Checkbox
                    color="default"
                    checked={isSelected}
                    onChange={(e) => handleSelectOne(cover.id, e)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Grid size="grow">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="h6"
                        color="textPrimary"
                        sx={{
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {cover.title?.text ||
                          t("platformCustomCovers.item.fallbackTitle", {
                            index: index + 1 + page * rowsPerPage,
                          })}
                      </Typography>
                      {!cover.publishAt && (
                        <Chip
                          size="small"
                          color="warning"
                          label={t("platformCustomCovers.item.notPublished")}
                        />
                      )}
                    </Stack>
                  </Grid>
                  <Grid container justifyContent="flex-end" size={3}>
                    {cover.publishAt && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="textPrimary"
                          align="right"
                        >
                          {t("platformCustomCovers.item.publishedAt", {
                            value: cover.publishAt,
                          })}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          align="right"
                        >
                          {t("platformCustomCovers.item.publishedDate", {
                            value: cover.publishAt,
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </StyledCard>
            </Grid>
          );
        })}
      </Grid>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count: c }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count: c !== -1 ? c : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
